import { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createCheckoutSession, getProductsConfig } from '@/lib/firebase-functions';
import { Skeleton } from '@/components/ui/skeleton';

interface AddCreditsModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function AddCreditsModal({ isOpen, onOpenChange }: AddCreditsModalProps) {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const [isYearly, setIsYearly] = useState(true);
  const [selectedCreditPack, setSelectedCreditPack] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const { data: products, isLoading: isLoadingProducts, error: productsError } = useQuery({
    queryKey: ['productsConfig'],
    queryFn: async () => {
      const result = await getProductsConfig();
      return result.data;
    },
    enabled: isOpen, // Solo hacer fetch cuando el modal está abierto
    staleTime: Infinity, // La configuración de productos no cambia, no necesita re-fetch
  });

  const handleCheckout = async (productKey: string | null, isSubscription: boolean, cycle?: 'monthly' | 'yearly') => {
    if (!productKey) {
      toast.error(t("addCredits.error.selection"));
      return;
    }
    if (!stripe) {
      toast.error(t("addCredits.error.stripe"));
      return;
    }

    setIsCheckoutLoading(true);

    try {
      const successUrl = `${window.location.origin}/feedbacks?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = window.location.origin;
      const locale = i18n.language.split('-')[0]; // Enviar 'es' o 'en'
      
      const params = { productKey, isSubscription, cycle, successUrl, cancelUrl, locale };
      console.log("Calling createCheckoutSession with:", params);

      const result = await createCheckoutSession(params);
      const { sessionId } = result.data;

      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        toast.error(error.message || t("addCredits.error.redirect"));
      }
    } catch (error: any) {
      toast.error(error.message || t("addCredits.error.session"));
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const baseCreditPack = products?.oneTimePurchases.find(p => p.usd === 5);
  const basePricePerCredit = baseCreditPack ? baseCreditPack.usd / baseCreditPack.credits : 0;

  const benefits = ['saveMoney', 'rollover', 'cancelAnytime', 'earlyUpdates'];

  const renderContent = () => {
    if (isLoadingProducts) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64 w-full" />)}
        </div>
      );
    }

    if (productsError) {
      return <p className="text-destructive text-center mt-4">{t('addCredits.error.loadConfig')}</p>;
    }

    if (!products) return null;

    return (
      <>
        {/* Bloque de Suscripciones con nuevo header */}
        <div className='flex justify-between items-center mt-4 mb-2'>
          <div>
            <h3 className='text-lg font-semibold'>{t('addCredits.subscriptions')}</h3>
            <p className="text-sm text-muted-foreground">{t('addCredits.subscriptionsSubtitleNew')}</p>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <Label htmlFor="billing-cycle" className="font-bold">{t('addCredits.monthly')}</Label>
            <Switch id="billing-cycle" checked={isYearly} onCheckedChange={setIsYearly} />
            <Label htmlFor="billing-cycle" className="font-bold text-primary">{t('addCredits.yearly')}</Label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {products.subscriptionPlans.map((plan) => {
            const price = isYearly ? plan.prices.yearly : plan.prices.monthly;
            const monthlyEquivalent = isYearly ? price.amount / 12 : price.amount;
            const pricePerCredit = price.amount / plan.credits;
            const savings = basePricePerCredit > 0 ? Math.round((1 - (pricePerCredit / basePricePerCredit)) * 100) : 0;

            return (
              <Card key={plan.name} className="flex flex-col">
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{t('addCredits.creditsPerMonth', { count: plan.credits })}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="flex-grow">
                    <p className="text-3xl font-bold">${monthlyEquivalent.toFixed(2)}<span className="text-sm font-normal text-muted-foreground">{t('addCredits.perMonth')}</span></p>
                    {isYearly && (
                      <>
                        <p className="text-sm text-muted-foreground">{t('addCredits.billedYearly', { amount: price.amount.toFixed(2) })}</p>
                        <p className="text-sm font-semibold text-primary mt-1">{t('addCredits.yearlyCredits', { count: plan.credits * 12 })}</p>
                      </>
                    )}
                    <ul className="space-y-2 text-sm text-muted-foreground mt-4">
                      {benefits.map(benefit => {
                        if (benefit === 'saveMoney' && savings <= 0) return null;
                        return (
                          <li key={benefit} className="flex items-center">
                            <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            <span>{t(`addCredits.benefits.${benefit}`, { percent: savings })}</span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                  <Button 
                    className="w-full mt-4" 
                    onClick={() => handleCheckout(plan.name, true, isYearly ? 'yearly' : 'monthly')}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {isCheckoutLoading ? t('addCredits.processing') : t('addCredits.subscribe')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bloque de Compra de Créditos */}
        <Card className="mt-6 bg-muted/40 w-full">
          <CardHeader>
            <CardTitle className="text-lg">{t('addCredits.creditsTitle')}</CardTitle>
            <CardDescription>{t('addCredits.creditsSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-full sm:w-2/3">
              <Select onValueChange={setSelectedCreditPack} value={selectedCreditPack || undefined}>
                <SelectTrigger>
                  <SelectValue placeholder={t('addCredits.selectPackage')} />
                </SelectTrigger>
                <SelectContent>
                  {products.oneTimePurchases.map((pack) => (
                    <SelectItem key={pack.name} value={pack.name}>
                      {t('addCredits.packageOption', { credits: pack.credits.toLocaleString(), usd: pack.usd })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              className="w-full sm:w-1/3"
              onClick={() => handleCheckout(selectedCreditPack, false)}
              disabled={isCheckoutLoading || !selectedCreditPack}
            >
              {isCheckoutLoading && selectedCreditPack ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isCheckoutLoading && selectedCreditPack ? t('addCredits.processing') : t('addCredits.buyNow')}
            </Button>
          </CardContent>
        </Card>
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('addCredits.title')}</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

